// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type CreateShipmentBody,
  type Facility,
  type Project,
  type Shipment,
  today,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { DateInputWrapped } from 'components/Mantine/DateInputWrapped';
import { FacilitySelect } from 'components/Mantine/GlobalFilters/FacilitySelect';
import { ProjectSelect } from 'components/Mantine/GlobalFilters/ProjectSelect';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import type { WithNullable } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

type ValidatedFormBody = Pick<CreateShipmentBody, 'name' | 'description' | 'shipByDate'> & {
  project: Pick<Project, 'projectId' | 'projectName'>;
  facility: Pick<Facility, 'facilityId' | 'facilityName'>;
};

type FormBody = WithNullable<ValidatedFormBody>;

type Props = ModalRegistryManagedProps & {
  defaultValues?: Partial<FormBody>;
  onShipmentCreated?: (shipment: Shipment) => void;
};

export const NewShipmentForm = ({
  defaultValues,
  onShipmentCreated,
}: Pick<Props, 'defaultValues' | 'onShipmentCreated'>) => {
  const navigate = useEvolveNavigate('Shipping Orders');

  const form = useForm<FormBody>({
    initialDirty: {
      name: !!defaultValues?.name,
    },
    initialValues: {
      project: defaultValues?.project ?? null,
      facility: defaultValues?.facility ?? null,
      name: defaultValues?.name ?? '',
      description: defaultValues?.description ?? '',
      shipByDate: defaultValues?.shipByDate ?? null,
    },
    validate: {
      facility: (v) => !v,
      project: (v) => !v,
      name: (v) => !v,
    },
  });

  const { apiCall: createShipmentApiCall } = useEvolvePost('shop/shipment');

  const onSubmit = async (rawValues: typeof form.values) => {
    // Cast is okay, validates as form.validate()
    const { project, facility, ...values } = rawValues as ValidatedFormBody;
    const shipment = await createShipmentApiCall({
      projectId: project.projectId,
      facilityId: facility.facilityId,
      ...values,
    });
    if (isNotNil(onShipmentCreated)) {
      onShipmentCreated(shipment);
    } else {
      void navigate(`/shipping-orders/${shipment.shipmentId}`);
    }
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <ProjectSelect
        label="Project"
        required
        autoSelectWhenOnlyOneOption
        {...form.getInputProps('project')}
        onChange={(p) => {
          form.setFieldValue('project', p);
          if (isNotNil(p?.defaultFacilityId)) {
            // Pre-fill facility from the project's default
            form.setFieldValue('facility', {
              facilityId: p.defaultFacilityId,
              facilityName: p.defaultFacilityName ?? '',
            });
          }
        }}
      />
      <FacilitySelect label="Facility" required autoSelectWhenOnlyOneOption {...form.getInputProps('facility')} />
      <TextInput label="Shipping Order Name" w="100%" maxLength={64} withAsterisk {...form.getInputProps('name')} />
      <DateInputWrapped
        w="100%"
        label="Ship By Date"
        description="The requested date that the shipment must be shipped by."
        minDate={today()}
        clearable
        {...form.getInputProps('shipByDate')}
      />
      <Textarea label="Description" maxLength={1024} minRows={6} autosize {...form.getInputProps('description')} />
    </>
  );
};

export const NewShipmentModal = ({ defaultValues, onShipmentCreated, ...props }: Props) => (
  <FormModal title="Create shipping order" size="md" confirmationText="Create" type="submit" {...props}>
    <NewShipmentForm defaultValues={defaultValues} onShipmentCreated={onShipmentCreated} />
  </FormModal>
);
